import React, { useState, useEffect } from 'react';
import { Dropdown } from '@fluentui/react/lib/Dropdown';
import { DistanceInput, TimeSpanPicker } from '@race-planner-web/form-components';
import { useAppAnalytics, EventName } from '@race-planner-web/app-analytics-bridge';
import { convertSecondsPerKilometerOrMileToSecondsPerMeter } from '@race-planner/unit-converters';
import { calculatePace, calculateRunWalkWalkInterval } from '@race-planner/calculations';
import { unitOptions } from './constants';
import { useUnexpectedInputErrorDialog } from './unexpected-input-error-dialog';
import { CalculateButton, HighlighDisabled } from './styled';

export const WalkIntervalCalcView = () => {
    const [unit, setUnit] = useState(unitOptions[0]);
    const { unitsOfLength, paceUnit } = unit;
    const [distanceInMeters, setDistanceInMeters] = useState(0);
    const [
        isDistanceAutoSelection,
        setIsDistanceAutoSelection
    ] = useState(true);
    const [timeInSeconds, setTimeInSeconds] = useState(0);
    const [runPaceInSeconds, setRunPaceInSeconds] = useState(0);
    const [walkPaceInSeconds, setWalkPaceInSeconds] = useState(0);
    const [runIntervalInSeconds, setRunIntervalInSeconds] = useState(0);
    const [walkIntervalInSeconds, setWalkIntervalInSeconds] = useState(0);
    const { sendEvent } = useAppAnalytics();
    const { openUnexpectedInputErrorDialog } = useUnexpectedInputErrorDialog();

    useEffect(() => {
        setIsDistanceAutoSelection(false);
    }, [isDistanceAutoSelection]);

    const onUnitChange = (event, option) => {
        setUnit(option);
    };

    const onCalculateWalkInt = () => {
        const roundedValue = Math.round(
            calculateRunWalkWalkInterval({
                timeInSeconds,
                distanceInMeters,
                runIntervalInSeconds,
                runPaceInSeconds,
                walkPaceInSeconds,
                unitsOfLength
            })
        );
        const shouldOpenErrorDialog = roundedValue <= 0;
        setWalkIntervalInSeconds(!shouldOpenErrorDialog ? roundedValue : 0);

        sendEvent({
            name: EventName.calculateInterval,
            attributes: {
                time_in_seconds: `${timeInSeconds}`,
                distance_in_meters: `${distanceInMeters}`,
                pace_in_seconds_per_meter: `${calculatePace({
                    timeInSeconds,
                    distanceInMeters
                })}`,
                running_interval_in_seconds: `${runIntervalInSeconds}`,
                walking_interval_in_seconds: `${roundedValue}`,
                running_pace_in_seconds_per_meter:
                    `${convertSecondsPerKilometerOrMileToSecondsPerMeter({
                        paceInSeconds: runPaceInSeconds,
                        unitsOfLength
                    })}`,
                walking_pace_in_seconds_per_meter:
                    `${convertSecondsPerKilometerOrMileToSecondsPerMeter({
                        paceInSeconds: walkPaceInSeconds,
                        unitsOfLength
                    })}`,
                is_treated_as_error: shouldOpenErrorDialog
            }
        });

        shouldOpenErrorDialog && openUnexpectedInputErrorDialog({
            message: 'Walking interval can not be calculated with the given input. Change input and calculate walking interval again.'
        });
    };

    return (
        <>
            <Dropdown
                label='Units'
                options={unitOptions}
                selectedKey={unit.key}
                onChange={onUnitChange}
            />
            <DistanceInput
                value={distanceInMeters}
                onValueChange={setDistanceInMeters}
                unitsOfLength={unitsOfLength}
                isAutoSelection={isDistanceAutoSelection}
            />
            <TimeSpanPicker
                label='Time'
                value={timeInSeconds}
                onValueChange={setTimeInSeconds}
            />
            <TimeSpanPicker
                label='Running interval'
                value={runIntervalInSeconds}
                onValueChange={setRunIntervalInSeconds}
            />
            <TimeSpanPicker
                label={`Running pace (${paceUnit})`}
                value={runPaceInSeconds}
                onValueChange={setRunPaceInSeconds}
                shouldDisplayDays={false}
                shouldDisplayHours={false}
            />
            <TimeSpanPicker
                label={`Walking pace (${paceUnit})`}
                value={walkPaceInSeconds}
                onValueChange={setWalkPaceInSeconds}
                shouldDisplayDays={false}
                shouldDisplayHours={false}
            />
            <CalculateButton
                onClick={onCalculateWalkInt}
            >
                Calculate Walking Interval
            </CalculateButton>
            <HighlighDisabled>
                <TimeSpanPicker
                    disabled
                    label='Walking interval'
                    value={walkIntervalInSeconds}
                />
            </HighlighDisabled>
        </>
    );
};
